import CONFIG from 'Config/settings';
import analytics from 'Utils/analytics/analytics';
import {
  ANALYTICS_USER_ACTIONS,
  ANALYTICS_ACTIONS_LABELS,
  ANALYTICS_FIELD_MAPPING
} from 'Utils/analytics/analyticsConstants';
import { PROFILE_TYPES } from 'Redux/profile/profileConstants';
import { readAuthTokenCookie, getDecodedAuthToken } from 'Utils/auth/authUtils';
import JwtDecode from 'jwt-decode';

const getAnalyticsFormAction = form => {
  if (/^regular-/.test(form)) return 'regular_form';
  if (/^family-/.test(form)) return 'family_form';
  return undefined;
};

const userDisplayedSignupPage = profileType => {
  if (CONFIG.ENV === 'production') {
    analytics.sendUserActionAnalyticsEvent(
      ANALYTICS_USER_ACTIONS[
        `SIGNUP_DISPLAY_${profileType.toUpperCase()}_PROFILE`
      ],
      ANALYTICS_ACTIONS_LABELS.SUCCESS,
      null
    );
  }
};

const userLoggedIn = authResp => {
  if (CONFIG.ENV === 'production') {
    // Ugly cookie consent check for France, this should be removed when the cookie consent is available for all markets
    if (
      /FR/i.test(CONFIG.COUNTRY) &&
      !window.ikea.cookieConsent.hasConsent(4)
    ) {
      return;
    }

    const idToken = authResp.id_token || authResp.idToken;
    const decodedIdToken = JwtDecode(idToken);
    const customerType =
      decodedIdToken['https://accounts.ikea.com/customerType'];
    const profileType =
      customerType === 'business'
        ? PROFILE_TYPES.BUSINESS
        : decodedIdToken['https://accounts.ikea.com/loyaltyPrograms'].some(
            program => program === 'IKEA_FAMILY'
          )
        ? PROFILE_TYPES.FAMILY
        : PROFILE_TYPES.REGULAR;
    const customerId = decodedIdToken['https://accounts.ikea.com/partyUId'];
    analytics.sendUserActionAnalyticsEvent(
      ANALYTICS_USER_ACTIONS[`LOGIN_${profileType.toUpperCase()}_PROFILE`],
      ANALYTICS_ACTIONS_LABELS.SUCCESS,
      customerId
    );
  }
};

const userLoggedInInternal = (profileType, customerId) => {
  if (CONFIG.ENV === 'production') {
    analytics.sendUserActionAnalyticsEvent(
      ANALYTICS_USER_ACTIONS[`LOGIN_${profileType.toUpperCase()}_PROFILE`],
      ANALYTICS_ACTIONS_LABELS.SUCCESS,
      customerId
    );
  }
};

const userCompletedSignup = (profileType, noDelay) => {
  if (CONFIG.ENV === 'production') {
    analytics.sendUserActionAnalyticsEvent(
      ANALYTICS_USER_ACTIONS[
        `SIGNUP_COMPLETED_${profileType.toUpperCase()}_PROFILE`
      ],
      ANALYTICS_ACTIONS_LABELS.SUCCESS,
      null,
      noDelay
    );
  }
};

const userUpdatedProfile = profileType => {
  if (CONFIG.ENV === 'production') {
    analytics.sendUserActionAnalyticsEvent(
      ANALYTICS_USER_ACTIONS.UPDATE_PROFILE,
      ANALYTICS_ACTIONS_LABELS[`${profileType.toUpperCase()}_PROFILE_UPDATE`],
      null
    );
  }
};

const userUpgradedProfile = () => {
  if (CONFIG.ENV === 'production') {
    analytics.sendUserActionAnalyticsEvent(
      ANALYTICS_USER_ACTIONS.UPDATE_PROFILE,
      ANALYTICS_ACTIONS_LABELS.REGULAR_PROFILE_UPGRADE,
      null
    );
  }
};

const userUpdatedUserLogin = profileType => {
  if (CONFIG.ENV === 'production') {
    analytics.sendUserActionAnalyticsEvent(
      ANALYTICS_USER_ACTIONS.UPDATE_PROFILE,
      ANALYTICS_ACTIONS_LABELS[
        `${profileType.toUpperCase()}_PROFILE_USER_LOGIN_UPDATE`
      ],
      null
    );
  }
};

const userDisplayedDashboard = () => {
  if (CONFIG.ENV === 'production') {
    analytics.sendPageViewAnalyticsEvent();
  }
};

const userDeletedProfile = profileType => {
  if (CONFIG.ENV === 'production') {
    analytics.sendUserActionAnalyticsEvent(
      ANALYTICS_USER_ACTIONS.DELETE_PROFILE,
      ANALYTICS_ACTIONS_LABELS[`${profileType.toUpperCase()}_PROFILE_DELETE`],
      null
    );
  }
};

const userProfileFormUpdate = ({ status, form }) => {
  if (CONFIG.ENV === 'production') {
    const { partyUid, profileType } = getDecodedAuthToken(
      readAuthTokenCookie()
    );

    const label = form.replaceAll(/-/g, '_').concat(`_${status}`);
    analytics.sendProfileActionAnalyticsEvent({
      label,
      partyUid,
      action: getAnalyticsFormAction(form),
      profileType
    });
  }
};

const userProfileFieldUpdate = ({ value, field }) => {
  if (CONFIG.ENV === 'production') {
    const { partyUid, profileType } = getDecodedAuthToken(
      readAuthTokenCookie()
    );
    const action = ANALYTICS_FIELD_MAPPING[field];
    const label = field.replaceAll(/-/g, '_').concat(`_${value}`);
    analytics.sendProfileActionAnalyticsEvent({
      label,
      partyUid,
      action,
      profileType
    });
  }
};

const userProfileUpdateError = ({ status, form, errorKey, errorMessage }) => {
  if (CONFIG.ENV === 'production') {
    const { partyUid, profileType } = getDecodedAuthToken(
      readAuthTokenCookie()
    );

    const label = form.replaceAll(/-/g, '_').concat(`_${status}`);
    analytics.sendProfileErrorActionAnalyticsEvent({
      label,
      partyUid,
      action: getAnalyticsFormAction(form),
      profileType,
      errorKey,
      errorMessage
    });
  }
};

const ciePagePopup = (action, label) => {
  if (CONFIG.ENV === 'production') {
    analytics.sendUserActionAnalyticsEvent(action, label, null, true);
  }
};

export default {
  userDisplayedSignupPage,
  userLoggedIn,
  userLoggedInInternal,
  userCompletedSignup,
  userUpdatedProfile,
  userUpgradedProfile,
  userUpdatedUserLogin,
  userDisplayedDashboard,
  userDeletedProfile,
  userProfileFormUpdate,
  userProfileUpdateError,
  userProfileFieldUpdate,
  ciePagePopup,
  getAnalyticsFormAction
};
